import React, { useContext, useState } from 'react';
import {
  AppBar, Toolbar, Typography, Button, Box,
  IconButton, Menu, MenuItem, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from './context/AuthContext';
import { NetworkStatusContext } from './context/NetworkStatusContext';
import { useCredits } from './context/CreditsContext';
import { signOut } from 'firebase/auth';
import { auth } from './firebase';
import logoUrl from './mindengage-logo-333.png';
import BuyCreditsDialog from  "./components/BuyCreditsDialog"

const TopBar = ({ isDesktop, onMobileDrawerToggle }) => {
  const { user } = useContext(AuthContext);
  const status   = useContext(NetworkStatusContext);
  const { balance, used, quota, refreshCredits } = useCredits();
  const navigate = useNavigate();
  const location = useLocation();

  const [userMenuAnchor,   setUserMenuAnchor]   = useState(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [buyOpen,setBuyOpen] = useState(false);

  const handleUserClick = (event) => {
    setUserMenuAnchor(event.currentTarget);
    refreshCredits();
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleMobileMenuOpen  = (e) => { setMobileMenuAnchor(e.currentTarget); refreshCredits(); };
  const handleMobileMenuClose = () =>   setMobileMenuAnchor(null);

  let mode = '';
  switch (location.pathname) {
    case '/edit':                   mode = 'Course Editor';        break;
    case '/generate':               mode = 'Course Builder';       break;
    case '/code-generator':         mode = 'Code Example';         break;   
    case '/selfquiz-generator':     mode = 'Quiz Builder';         break;         
    case '/insight-generator':      mode = 'Concept Explainer';    break;      
    case '/simulation-generator':   mode = 'Simulation Builder (Experimental)';    break;
    case '/diagram-generator':      mode = 'Diagram Builder';      break;    
    case '/presentation-generator': mode = 'Deck Builder';         break;         
    case '/voice-cloning':          mode = 'Voice Cloning';        break;
    case '/generate-speech':        mode = 'Speech Synthesizer';   break;
    case "/reference-uploads":      mode = 'Upload Reference';     break;
    case '/teacher-api-keys':       mode = 'API Keys';             break;
    case '/exam-generator':         mode = 'Exam Builder';         break;  
    case '/publish':                mode = 'Publish to Google Classroom';              break; 
    case '/me-publish':             mode = 'Publish to MindEngage LMS';              break;         
    case '/download-course':        mode = 'Download Course';      break;              
    default:                        mode = 'MindEngage Author';
  }

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/signin');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1, overflow: 'visible' }}>
      <Toolbar>
        {!isDesktop && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMobileDrawerToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box component="img" src={logoUrl} alt="Logo" sx={{ height: 60, mr: 2 }} />

        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" noWrap>MindEngage Author</Typography>
          {mode !== 'MindEngage Author' && (
            <Typography variant="subtitle2" noWrap>{mode}</Typography>
          )}
        </Box>

        <Typography
          variant="subtitle2"
          noWrap
          sx={{ mr: 2, display: { xs: 'none', md: 'block' } }}
        >
          {status === 'online' ? 'SERVER-ON' : 'SERVER-OFF'}
        </Typography>

        <Button
          variant="text"
          color="inherit"
          disableRipple
          sx={{
            mr: 2,
            textTransform: 'none',
            fontSize: '0.875rem',
            lineHeight: 1.66,
            display: { xs: 'none', md: 'inline-flex' },
          }}
          onClick={() => setBuyOpen(true)}
        >
          Credits: {balance ?? '…'}
        </Button>
        <BuyCreditsDialog open={buyOpen} onClose={() => setBuyOpen(false)} />



        {user && (
          <>
            <Typography
              variant="subtitle2"
              noWrap
              sx={{ mr: 1, cursor: 'pointer', display: { xs: 'none', md: 'block' } }}
              onClick={handleUserClick}
            >
              {user.displayName || user.email}
            </Typography>
            <Menu
              anchorEl={userMenuAnchor}
              open={Boolean(userMenuAnchor)}
              onClose={handleUserMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {user.displayName && <MenuItem onClick={handleUserMenuClose}>{user.displayName}</MenuItem>}
              <MenuItem onClick={handleUserMenuClose}>{user.email}</MenuItem>
              {balance !== null && (
                <>
                  <Divider />
                  {/* FIXED line ↓ */}
                  <MenuItem disabled>Balance: {balance}{quota != null && ` / ${quota}`}</MenuItem>
                  {/* FIXED line ↑ */}
                  <MenuItem disabled>Used this month: {used}</MenuItem>
                </>
              )}
            </Menu>
          </>
        )}

        {user ? (
          <Button
            color="inherit"
            onClick={handleSignOut}
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          >
            Sign Out
          </Button>
        ) : (
          <Button
            color="inherit"
            component={Link}
            to="/signin"
            sx={{ display: { xs: 'none', md: 'inline-flex' } }}
          >
            Sign In
          </Button>
        )}
        
        <IconButton
          color="inherit"
          component="a"
          href="https://mindengage.gitbook.io/mindengage-docs/"
          target="_blank"
          rel="noopener"
          sx={{ mr: 1, display: { xs: 'none', md: 'inline-flex' } }}
        >
          <HelpOutlineIcon />
        </IconButton>

        {!isDesktop && (
          <>
            <IconButton color="inherit" onClick={handleMobileMenuOpen} sx={{ mr: 0, ml: 0.5 }}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={mobileMenuAnchor}
              open={Boolean(mobileMenuAnchor)}
              onClose={handleMobileMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              <MenuItem disabled>{status === 'online' ? 'SERVER-ON' : 'SERVER-OFF'}</MenuItem>
              <MenuItem onClick={() => { setBuyOpen(true); handleMobileMenuClose(); }}>
                Credits: {balance ?? '…'}
              </MenuItem>
              <MenuItem onClick={() => { window.open('https://mindengage.gitbook.io/mindengage-docs/', '_blank'); handleMobileMenuClose(); }}>
                Help
              </MenuItem>
              {user && <MenuItem disabled>{user.displayName || user.email}</MenuItem>}
              {user ? (
                <MenuItem onClick={() => { handleSignOut(); handleMobileMenuClose(); }}>
                  Sign Out
                </MenuItem>
              ) : (
                <MenuItem component={Link} to="/signin" onClick={handleMobileMenuClose}>
                  Sign In
                </MenuItem>
              )}
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default TopBar;
